import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import { ablageHochladen, ablageUrl, verkleinern } from "../daten/routen";
import { useGeraetefotos } from "../daten/haken";
import { Fehler } from "../daten/klient";
import "../stile/handy.css";

/* ══════════════════════════════════════════════════════════════════
   Fotos an einem Protokoll.

   Vorher stand hier „2 Fotos" — eine Auskunft, die niemandem hilft. Wer
   ein Übergabeprotokoll liest, will den Kratzer sehen, über den er gleich
   mit dem Kunden spricht.

   Die Bilder liegen in einem **nicht öffentlichen** Behälter: Auf einem
   Übergabeprotokoll steht eine Unterschrift. Herausgegeben werden sie über
   eine signierte URL, die abläuft — eine dauerhafte wäre eine dauerhafte
   Freigabe. Deshalb wird sie erst geholt, wenn die Bilder auch gezeigt
   werden, und nicht auf Vorrat.
   ══════════════════════════════════════════════════════════════════ */

function Bild({ pfad, aufmachen }: { pfad: string; aufmachen: (url: string) => void }) {
  const [url, setzeUrl] = useState<string | null>(null);
  const [kaputt, setzeKaputt] = useState(false);

  useEffect(() => {
    let lebt = true;
    ablageUrl(pfad).then(
      (u) => { if (lebt) setzeUrl(u); },
      () => { if (lebt) setzeKaputt(true); },
    );
    return () => { lebt = false; };
  }, [pfad]);

  if (kaputt) return <div className="foto foto-fehlt" aria-hidden="true" />;
  if (!url) return <div className="foto foto-laedt" aria-hidden="true" />;
  return (
    <button type="button" className="foto" onClick={() => aufmachen(url)}>
      <img src={url} alt="" loading="lazy" />
    </button>
  );
}

export function Fotos({ pfade }: { pfade: string[] }) {
  const t = useT();
  const [gross, setzeGross] = useState<string | null>(null);

  useEffect(() => {
    if (!gross) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeGross(null); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [gross]);

  if (!pfade.length) return <span className="foto-keine">{t("vorgang.protFotosKeine")}</span>;

  return (
    <>
      <div className="foto-reihe" role="group" aria-label={t("vorgang.protFotos", { zahl: pfade.length })}>
        {pfade.map((p) => <Bild key={p} pfad={p} aufmachen={setzeGross} />)}
      </div>

      {/* Eine Lupe, kein zweites Fenster: Wer ein Foto vergrößert, will es
          ansehen und zurück — nicht navigieren. */}
      {gross ? (
        <div className="foto-lupe" onClick={() => setzeGross(null)} role="presentation">
          <img src={gross} alt="" />
          <span className="foto-zu">{t("allgemein.schliessen")}</span>
        </div>
      ) : null}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Fotos aufnehmen — der Weg, den es bis zum 16.09.2026 nicht gab.

   `POST /api/ablage` stand da, `Fotos` oben konnte anzeigen, und
   dazwischen fehlte alles: Im ganzen Web-Ordner gab es keinen Aufruf,
   der eine Datei hinaufschickt. Ein Übergabeprotokoll ohne Bild ist bei
   einer Maschine ohne Personal der einzige Nachweis, den es nicht gibt
   (09-mobile.md, Lisa, 09.09.2026).

   **`capture="environment"` öffnet auf dem Telefon die Rückkamera**, auf
   dem Schirm den Dateiwähler — dieselbe Marke, zwei Geräte, kein
   zweiter Knopf. `multiple` bleibt an: Fünf Perspektiven hintereinander
   sind fünf Griffe, und der Fahrer hat eine Hand frei.

   **Daneben die Mediathek** (18.09.2026, Luca zur Ortsaufnahme: *„In den
   Vor-Ort-Aufnahmen möchten wir auch Fotos erfassen."*). `capture` ist
   auf dem Telefon keine Empfehlung, sondern eine Anweisung: Wer darauf
   tippt, bekommt die Kamera und **nur** die Kamera — kein Weg zu einem
   Bild, das schon da ist. Genau das ist bei einer Ortsaufnahme der
   Regelfall: Der Vertriebler hat das Anlagendatenblatt und den Lageplan
   längst abfotografiert, bevor er auf diesen Schirm kommt. Deshalb zwei
   Flächen nebeneinander — dasselbe Feld, einmal mit `capture` und einmal
   ohne. Ein Kasten, der beides könnte, gibt es im Browser nicht.

   **Verkleinert wird auf dem Gerät**, vor dem Hochladen. Sonst geht ein
   Zwölf-Megapixel-Bild über Baustellenfunk, und das Protokoll lädt nie
   fertig hoch.

   **Ohne Netz bleibt das Bild da** (seit dem 16.09.2026, Migration 0113).
   Bis dahin stand nach dem Auslösen „Das Foto ließ sich nicht ablegen"
   und das Bild war weg — auf einer Baustelle, auf der man es nicht noch
   einmal machen kann, weil die Maschine schon fährt. Jetzt hält der
   Aufrufer den Blob (`blobs`), und die Warteschlange legt ihn ab, sobald
   Empfang da ist. Wer `blobs` nicht mitgibt, bekommt den alten Weg: Ein
   Bild, das nicht hinaufgeht, ist dort ein Fehler.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Bild, das noch auf dem Gerät liegt — gezeigt aus dem Blob selbst. */
function Blobbild({ blob }: { blob: Blob }) {
  const [url, setzeUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setzeUrl(u);
    // Ohne `revokeObjectURL` hält der Browser jedes Bild im Speicher, bis
    // die Seite neu lädt — bei zehn Baustellenfotos ist das spürbar.
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  if (!url) return <div className="foto foto-laedt" aria-hidden="true" />;
  return <span className="foto"><img src={url} alt="" /></span>;
}

export function Fotoaufnahme({ pfade, setzePfade, blobs, setzeBlobs }: {
  /** Die schon abgelegten Pfade. Der Aufrufer hält sie, nicht dieser Baustein. */
  pfade: string[];
  setzePfade: (neu: string[]) => void;
  /** Bilder, die noch nicht abgelegt sind — sie gehen über die Warteschlange. */
  blobs?: Blob[];
  setzeBlobs?: (neu: Blob[]) => void;
}) {
  const t = useT();
  const wahl = useRef<HTMLInputElement | null>(null);
  const mediathek = useRef<HTMLInputElement | null>(null);
  const [laedt, setzeLaedt] = useState(0);
  const [fehler, setzeFehler] = useState<string | null>(null);
  const warten = blobs ?? [];

  async function aufnehmen(dateien: FileList | null) {
    if (!dateien?.length) return;
    setzeFehler(null);
    const neue: string[] = [];
    const behalten: Blob[] = [];
    for (const datei of Array.from(dateien)) {
      setzeLaedt((n) => n + 1);
      const klein = await verkleinern(datei).catch(() => null);
      if (!klein) {
        setzeFehler("fotos.gingNicht");
        setzeLaedt((n) => n - 1);
        continue;
      }
      try {
        if (setzeBlobs && navigator.onLine === false) throw new Fehler("netz", 0);
        neue.push(await ablageHochladen(klein, "foto"));
      } catch (e) {
        const amNetz = !(e instanceof Fehler) || e.schluessel === "netz" || e.status === 0
          || e.status >= 500;
        if (setzeBlobs && amNetz) {
          // Das Bild bleibt auf dem Gerät und geht mit der Eintragung hinaus.
          behalten.push(klein);
        } else {
          // Der Satz sagt, WAS nicht ging: zu groß, falsches Format, oder
          // schlicht kein Netz. „Es hat nicht geklappt" hilft auf dem Hof
          // niemandem weiter.
          setzeFehler(
            e instanceof Fehler && e.schluessel === "ablage.zu_gross" ? "fotos.zuGross"
              : e instanceof Fehler && e.schluessel === "ablage.typ_nicht_erlaubt" ? "fotos.typFalsch"
                : "fotos.gingNicht",
          );
        }
      } finally {
        setzeLaedt((n) => n - 1);
      }
    }
    if (neue.length) setzePfade([...pfade, ...neue]);
    if (behalten.length && setzeBlobs) setzeBlobs([...warten, ...behalten]);
    // Dieselbe Datei zweimal hintereinander wählen zu können heißt, den
    // Wähler zu leeren — sonst feuert `change` beim zweiten Mal nicht.
    if (wahl.current) wahl.current.value = "";
    if (mediathek.current) mediathek.current.value = "";
  }

  return (
    <div className="foto-aufnahme">
      <div className="foto-reihe">
        {pfade.map((p) => (
          <span className="foto-mit-weg" key={p}>
            <Bild pfad={p} aufmachen={() => {}} />
            <button
              type="button"
              className="foto-weg"
              onClick={() => setzePfade(pfade.filter((x) => x !== p))}
              aria-label={t("fotos.entfernen")}
            >
              <span className="foto-weg-zeichen" aria-hidden="true">{t("fotos.entfernenZeichen")}</span>
            </button>
          </span>
        ))}
        {warten.map((b, i) => (
          <span className="foto-mit-weg" key={`blob-${i}-${b.size}`}>
            <Blobbild blob={b} />
            <button
              type="button"
              className="foto-weg"
              onClick={() => setzeBlobs?.(warten.filter((x) => x !== b))}
              aria-label={t("fotos.entfernen")}
            >
              <span className="foto-weg-zeichen" aria-hidden="true">{t("fotos.entfernenZeichen")}</span>
            </button>
          </span>
        ))}
        {Array.from({ length: laedt }, (_, i) => (
          <div className="foto foto-laedt" key={`laedt-${i}`} aria-hidden="true" />
        ))}
      </div>

      {warten.length ? (
        <span className="foto-wartet">
          {t(warten.length === 1 ? "offline.fotosWartetEins" : "offline.fotosWarten", { zahl: warten.length })}
        </span>
      ) : null}

      <div className="foto-griffe">
        <label className="foto-knopf">
          <input
            ref={wahl}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={(e) => void aufnehmen(e.target.files)}
          />
          {/* Kein Symbol davor (Durchgang AD1, 16.09.2026). Hier stand ein
              Kamera-Emoji, und Luca: *„ein Emoji bei ‚Foto aufnehmen' ist
              der absolute KI-Slop."* Einen Symbolsatz hat die Anwendung
              nicht; das Wort sagt, was der Knopf tut. */}
          {t(pfade.length ? "fotos.nochEins" : "fotos.aufnehmen")}
        </label>

        {/* Dasselbe Feld ohne `capture`: der Weg zu dem, was schon da ist.
            Am Schirm tun beide dasselbe — dort gibt es keine Kamera, und
            zwei Flächen nebeneinander sind kein Schaden. */}
        <label className="foto-knopf leise">
          <input
            ref={mediathek}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => void aufnehmen(e.target.files)}
          />
          {t("fotos.ausMediathek")}
        </label>
      </div>

      {fehler ? <span className="foto-fehler">{t(fehler)}</span> : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Die Fotos einer Maschine — für die Maschinenakte.

   Luca, 16.09.2026: *„Die hängen natürlich am Protokoll, aber dann auch
   am Gerät."*

   **Es gibt keine zweite Ablage.** Dieser Baustein liest denselben
   Bestand über die Belegung (`GET /api/protokolle/fotos?ressource=`).

   **Jedes Bild steht in seinem Zusammenhang.** Vorgang, Kunde, Ausgabe
   oder Rücknahme, Zeitpunkt — und wenn im Protokoll ein Schaden stand,
   steht er darunter. Ein Schadensfoto ohne seine Geschichte ist eine
   Behauptung; mit Vorgang und Datum ist es ein Beleg (20-prozesse.md, D2).

   **Wo er noch hingehört (16.09.2026).** In die Maschinenakte —
   `masken/stamm/Akte.tsx`, `MaschinenBlatt`, als eigener Abschnitt neben
   „Anbauteile" und „Anschaffung":

       {!neu && id ? (
         <div className="pflege-teil">
           <span className="pflege-schild">{t("fotos.amGeraet")}</span>
           <Geraetefotos ressourceId={id} />
         </div>
       ) : null}

   `id` ist dort schon die Ressourcen-UUID, der Schlüssel steht in
   `de.json`. Die Datei gehört einem anderen Strang; deshalb steht die
   Zeile hier und nicht dort.
   ══════════════════════════════════════════════════════════════════ */

export function Geraetefotos({ ressourceId }: { ressourceId: string }) {
  const t = useT();
  const { data, isPending, isError } = useGeraetefotos(ressourceId);
  const [gross, setzeGross] = useState<string | null>(null);

  useEffect(() => {
    if (!gross) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setzeGross(null); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [gross]);

  if (isPending) return <div className="foto-reihe" aria-busy="true"><div className="foto foto-laedt" /></div>;
  if (isError) return <span className="foto-keine">{t("fotos.gingNicht")}</span>;
  if (!data?.length) return <span className="foto-keine">{t("fotos.amGeraetKeine")}</span>;

  return (
    <>
      <div className="geraetefotos">
        {data.map((f) => (
          <figure className="geraetefoto" key={`${f.protokollId}-${f.pfad}`}>
            <Bild pfad={f.pfad} aufmachen={setzeGross} />
            <figcaption>
              <b>{t(f.art === "ruecknahme" ? "fotos.beiRuecknahme" : "fotos.beiAusgabe")}</b>
              <span>{f.wann}</span>
              {/* Ohne Vorgang und Kunde ist es ein Bild von einem Bagger. */}
              <span className="leise">
                {[f.vorgang, f.kunde, f.ort].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
              </span>
              {f.schaden ? <span className="geraetefoto-schaden">{f.schaden}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>

      {gross ? (
        <div className="foto-lupe" onClick={() => setzeGross(null)} role="presentation">
          <img src={gross} alt="" />
          <span className="foto-zu">{t("allgemein.schliessen")}</span>
        </div>
      ) : null}
    </>
  );
}
